import React from 'react';
import { Typography, Card, Tag, Row, Col } from 'antd';
import { ArrowRight } from "@phosphor-icons/react";
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Ribbon } from '../../components/Ribbon';
import { GlowEffect } from '../../components/GlowEffect';
import { ParallaxShapes } from '../../components/ParallaxShapes';
import { standardVariants, containerVariants } from '../../utils/useAnimations';
import { AccessibleButton } from '../../components/AccessibleButton';
import { createTags, useAccessibility } from '../../utils/accessibility';
import { parcoursData } from '../../data/parcours';
import { getDateStatus } from '../../utils/utils';

const { Title, Paragraph } = Typography;


export const Parcours: React.FC = () => {
  const { getText } = useAccessibility();
  const navigate = useNavigate();

  const handleStartCourse = (slug: string) => {
    navigate(`/parcours/${slug}`);
  };

  return (
    <section 
      id="parcours" 
      className="relative overflow-visible flex-1"
      aria-labelledby="parcours-title"
      role="region"
    >
      <div className="z-[2] relative">
        <div className="z-2 max-w-[1280px] mx-auto max-w-[1440px] mx-auto p-20 pb-24">
          <motion.div
            variants={standardVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <Title 
              id="parcours-title"
              level={2} 
              className="!text-4xl !font-bold !text-center !mb-6"
            >
              Les parcours
            </Title>
            <Paragraph className="!text-neutral-600 !text-center !mb-10 !text-lg">
              Relevez ces défis pour comprendre les obstacles rencontrés<br/>par les personnes en situation de handicap
            </Paragraph>
          </motion.div>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            role="list"
            aria-label={getText('landmarks.coursesList')}
          >
            <Row gutter={[32, 32]}>
              {parcoursData.map((parcours) => {                
                let DataItems = createTags(parcours.typeParcoursIds[0])
                const Icon = DataItems.icon;
                const card = (
                  <Card  
                    className={`h-full relative border-none rounded-2xl bg-neutral-white/80 ${ parcours?.datePublication ? 'gradient-border-1 gradient-border-conic-vibrant bg-neutral-white/95' : 'gradient-border-1 gradient-border-radial-blue !bg-neutral-white/80}'} `} 
                    bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: (getDateStatus(parcours?.datePublication) !== 'old')?'flex-start':'space-between' }}
                    role="listitem"
                    aria-labelledby={`course-${parcours.id}-title`}
                  >
                    <div style={{display:'flex',flexDirection:'row',justifyContent:'space-between',marginBottom:(getDateStatus(parcours?.datePublication) !== 'old')?'0.75rem':0}}>
                      <Tag 
                        icon={<Icon weight="bold"/>} 
                        className={`tag tag-${DataItems.class}`}
                        aria-label={`Catégorie: ${DataItems.name}`}
                        style={{marginBottom:(getDateStatus(parcours?.datePublication) !== 'old')?0:'0.75rem'}}
                      >
                        {DataItems.name}
                      </Tag>
                      {(parcours.datePublication && getDateStatus(parcours?.datePublication) !== 'old' ) && 
                        <Ribbon>
                          <span aria-label={
                            (getDateStatus(parcours?.datePublication) === 'soon' ? "status.comingSoonBadge" : (getDateStatus(parcours?.datePublication) === "new") ? "status.newBadge":"")}>
                            {getDateStatus(parcours?.datePublication) === 'soon' ? "BIENTÔT" : (getDateStatus(parcours?.datePublication) === "new") ? "NOUVEAU":""}
                          </span>
                        </Ribbon>
                      }
                    
                    </div>
                    
                    <Title 
                      id={`course-${parcours.id}-title`}
                      level={4} 
                      className="!text-lg !font-semibold"
                    >
                      {parcours.nom}
                    </Title>
                    <Paragraph className="!text-base !leading-relaxed">
                      {parcours.details}
                    </Paragraph>
                    <Paragraph className="!text-base">
                      <strong>Mission :</strong> {parcours.objectif}
                    </Paragraph>
                    {getDateStatus(parcours?.datePublication) !== "soon" && (
                      <AccessibleButton 
                        type="primary"
                        className="mt-6 !w-max"
                        ariaLabelKey="buttons.startCourse"
                        ariaDescribedBy={`course-${parcours.id}-title`}
                        onClick={() => handleStartCourse(parcours.slug)}
                      >
                        <span>Commencer le parcours</span>
                        <ArrowRight weight="bold" className="w-5 h-5 ml-1" aria-hidden="true" />
                      </AccessibleButton>
                    )}
                  </Card>
                );

                const wrapped = (getDateStatus(parcours?.datePublication)!== 'old') ? (
                  <>
                  <GlowEffect
                    blur='stronger'
                    scale={0.85}
                    className="rounded-2xl"
                  />
                  {card}
                  </>
                ) : (
                  card
                );

                return (
                  <Col xs={24} sm={12} lg={8} key={parcours.id} className="flex">
                    <motion.div variants={standardVariants}>
                      {wrapped}
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </div>
      </div>
      <ParallaxShapes/>
    </section>
  );
};